import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import client from '../api/client.js'

const EMPTY_WALLET = { availableCredit: 0, lockedCredit: 0, earnedCredit: 0 }
const EMPTY_TRUST = { trustLevel: 'NEW', trustPoints: 0 }
const EMPTY_HEALTH = { score: 0, healthLevel: 'N/A', components: [] }

export const useGrowthStore = defineStore('growth', () => {
  const dashboard = ref(null)
  const wallet = ref({ ...EMPTY_WALLET })
  const trust = ref({ ...EMPTY_TRUST })
  const health = ref({ ...EMPTY_HEALTH })
  const milestones = ref([])
  const achievements = ref([])
  const availableBadges = ref([])
  const referrals = ref([])
  const notifications = ref([])
  const spendOptions = ref([])
  const rewardRules = ref([])
  const campaigns = ref([])
  const loading = ref(false)
  const loadedGroups = ref(new Set())

  const trustLevel = computed(() => trust.value.trustLevel)
  const healthScore = computed(() => health.value.score ?? 0)
  const achievementCount = computed(() => achievements.value.length)
  const availableCredit = computed(() => wallet.value.availableCredit ?? 0)

  function buildPath(groupId, suffix) {
    return `/growth/community/${groupId}/${suffix}`
  }

  async function fetchDashboard(groupId) {
    loading.value = true
    try {
      const { data } = await client.get(buildPath(groupId, 'dashboard'))
      dashboard.value = data
      wallet.value = { ...EMPTY_WALLET, ...(data.wallet ?? {}) }
      trust.value = { ...EMPTY_TRUST, ...(data.trust ?? {}) }
      milestones.value = data.milestones ?? []
      achievements.value = data.achievements ?? []
      referrals.value = data.referrals ?? []
      notifications.value = data.notifications ?? []
      loadedGroups.value.add(groupId)
      return data
    } finally {
      loading.value = false
    }
  }

  async function fetchAchievements(groupId) {
    const { data } = await client.get(buildPath(groupId, 'achievements'))
    achievements.value = data.achievements ?? []
    availableBadges.value = data.availableBadges ?? []
    return data
  }

  async function fetchHealth(groupId) {
    const { data } = await client.get(buildPath(groupId, 'health'))
    health.value = { ...EMPTY_HEALTH, ...data }
    return data
  }

  async function fetchSpendOptions(groupId) {
    const { data } = await client.get(buildPath(groupId, 'spend-options'))
    wallet.value = { ...wallet.value, ...(data.wallet ?? {}) }
    spendOptions.value = data.options ?? []
    return data
  }

  async function spendCredits(groupId, option) {
    const { data } = await client.post(buildPath(groupId, 'spend'), {
      optionId: option.id ?? option.optionId ?? null,
      name: option.name,
      cost: option.cost,
    })
    if (data?.wallet) wallet.value = { ...wallet.value, ...data.wallet }
    return data
  }

  async function fetchRewardCampaigns() {
    const { data } = await client.get('/growth/admin/campaigns')
    campaigns.value = data
    return data
  }

  async function createCampaign(payload) {
    const { data } = await client.post('/growth/admin/campaigns', payload)
    campaigns.value.unshift(data)
    return data
  }

  async function updateCampaign(id, payload) {
    const { data } = await client.put(`/growth/admin/campaigns/${id}`, payload)
    const idx = campaigns.value.findIndex((c) => c.id === id)
    if (idx !== -1) campaigns.value[idx] = data
    return data
  }

  async function pauseCampaign(id) {
    await client.post(`/growth/admin/campaigns/${id}/pause`)
    const idx = campaigns.value.findIndex((c) => c.id === id)
    if (idx !== -1) campaigns.value[idx].status = 'PAUSED'
  }

  async function activateCampaign(id) {
    await client.post(`/growth/admin/campaigns/${id}/activate`)
    const idx = campaigns.value.findIndex((c) => c.id === id)
    if (idx !== -1) campaigns.value[idx].status = 'ACTIVE'
  }

  async function fetchRewardRules(params = {}) {
    const { data } = await client.get('/growth/admin/rules', { params })
    rewardRules.value = data
    return data
  }

  async function createRewardRule(payload) {
    const { data } = await client.post('/growth/admin/rules', payload)
    rewardRules.value.unshift(data)
    return data
  }

  async function updateRewardRule(id, payload) {
    const { data } = await client.put(`/growth/admin/rules/${id}`, payload)
    const idx = rewardRules.value.findIndex((r) => r.id === id)
    if (idx !== -1) rewardRules.value[idx] = data
    return data
  }

  async function toggleRewardRule(id, enabled) {
    const { data } = await client.patch(`/growth/admin/rules/${id}/toggle`, { enabled })
    const idx = rewardRules.value.findIndex((r) => r.id === id)
    if (idx !== -1) rewardRules.value[idx].enabled = enabled
    return data
  }

  async function deleteRewardRule(id) {
    await client.delete(`/growth/admin/rules/${id}`)
    rewardRules.value = rewardRules.value.filter((r) => r.id !== id)
  }

  return {
    dashboard, wallet, trust, health, milestones, achievements,
    availableBadges, referrals, notifications, spendOptions,
    rewardRules, campaigns, loading, loadedGroups,
    trustLevel, healthScore, achievementCount, availableCredit,
    fetchDashboard, fetchAchievements, fetchHealth, fetchSpendOptions,
    spendCredits, fetchRewardCampaigns, createCampaign, updateCampaign,
    pauseCampaign, activateCampaign, fetchRewardRules,
    createRewardRule, updateRewardRule, toggleRewardRule, deleteRewardRule,
  }
})
